import { ref } from 'vue'
import { jsonapiClient } from '@/api/client'
import type { WorkPeriod } from '@/composables/useWorkPeriods'
import type { SeasonSpec } from '@/lib/seasons'

// THE ORG'S SEASONS, read once and shared: which seasons each job type is sold
// into (the type's declarable périodes, in the shop's order). The one season
// menu — intake, the job header, « Renouveler ▾ », « Transférer ▾ » — asks
// this for a type's choices, so four doors cannot list four different menus.
// Module-level on purpose: the catalog is a handful of rows per org, and every
// desk row would otherwise fetch it.
const periods = ref<WorkPeriod[]>([])
const loadedFor = ref<string | null>(null)
let inflight: Promise<void> | null = null

export function useSeasonCatalog() {
  const load = async (orgId: string) => {
    if (loadedFor.value === orgId) return
    if (!inflight) {
      inflight = jsonapiClient
        .get('/api/work_periods', { params: { include: { job_templates: true }, page: { size: 100 } } })
        .then(response => {
          periods.value = response.data.data
          loadedFor.value = orgId
        })
        .catch(() => { /* a missing catalog only hides the menus; nothing else breaks */ })
        .finally(() => { inflight = null })
    }
    await inflight
  }

  // The season has been read or changed on another surface — forget the copy.
  const invalidate = () => { loadedFor.value = null }

  const seasonsForType = (typeId: string | null | undefined): SeasonSpec[] => {
    if (!typeId) return []
    return periods.value
      .filter(p => p.attributes.nature === 'declarable')
      .filter(p => (p.relationships?.job_templates?.data ?? []).some(r => r.id === typeId))
      // The feed's order IS the shop's order (derived server-side) — kept as is.
      .map(p => ({
        id: p.id, name: p.attributes.name, wrapsYear: p.attributes.wraps_year === true,
        // The served floor (this year, or last year for a wrapping season) —
        // where every menu of this season starts when history says nothing.
        earliestOpenableYear: p.attributes.earliest_openable_year,
        // The row's run (open → idle with jobs → latest closed), as the server picks it.
        currentRun: p.attributes.current_run
          ? { year: p.attributes.current_run.season_year, state: p.attributes.current_run.state }
          : null,
      }))
  }

  return { periods, load, invalidate, seasonsForType }
}
